import React from 'react';
import { Layers, Play, Download, Trash2, Sparkles, BookOpen } from 'lucide-react';
import { getDeckStats } from '../utils/srsEngine';

export default function DeckManager({ decks = [], onSelectDeck, onDeleteDeck }) {
  const exportToAnkiCSV = (deck) => {
    if (!deck?.flashcards) return;
    
    // Anki CSV format: Front, Back, Tags
    let csvContent = 'data:text/csv;charset=utf-8,';
    csvContent += 'Front,Back,Tags\n';

    deck.flashcards.forEach((card) => {
      const front = `"${(card.front || '').replace(/"/g, '""')}"`;
      const back = `"${(card.back || '').replace(/"/g, '""')}"`;
      const tags = `"${(card.tags || []).join(' ')}"`;
      csvContent += `${front},${back},${tags}\n`;
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${(deck.deckTitle || 'deck').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_anki.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToJSON = (deck) => {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(deck, null, 2));
    const link = document.createElement('a');
    link.setAttribute('href', dataStr);
    link.setAttribute('download', `${(deck.deckTitle || 'deck').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!decks || decks.length === 0) {
    return (
      <div className="bg-dark-700/30 rounded-2xl border border-dashed border-white/10 p-8 text-center text-white/50 backdrop-blur-xl">
        <Layers className="w-10 h-10 text-white/30 mx-auto mb-3" />
        <h3 className="font-display text-base font-bold text-white/80">No Saved Decks Yet</h3>
        <p className="text-xs text-white/40 mt-1 max-w-sm mx-auto">
          Upload lecture slides, notes, or handwritten images above to automatically generate your first active-recall deck.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display text-base font-bold text-white flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-saffron-400" />
          My Flashcard Decks ({decks.length})
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {decks.map((deck) => {
          const stats = getDeckStats(deck.flashcards || []);
          return (
            <div
              key={deck.id}
              className="bg-dark-700/50 border border-white/10 hover:border-saffron-500/40 rounded-2xl p-5 shadow-sm transition-all flex flex-col justify-between backdrop-blur-xl group"
            >
              <div>
                <div className="flex justify-between items-start mb-2">
                  <span className="px-2.5 py-0.5 bg-saffron-500/15 text-saffron-400 border border-saffron-500/20 text-[11px] font-semibold rounded-full">
                    {deck.exam || 'General'}
                  </span>
                  {stats.dueCount > 0 && (
                    <span className="px-2.5 py-0.5 bg-amber-500/15 text-amber-400 border border-amber-500/25 text-[11px] font-semibold rounded-full flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> {stats.dueCount} Due Today
                    </span>
                  )}
                </div>

                <h4 className="font-display text-base font-bold text-white mb-1 line-clamp-1 group-hover:text-saffron-300 transition-colors">
                  {deck.deckTitle}
                </h4>
                <p className="text-xs text-white/50 line-clamp-2 mb-4">
                  {deck.summary || 'Auto-generated flashcard study deck.'}
                </p>
              </div>

              <div>
                {/* Stats row */}
                <div className="grid grid-cols-3 gap-2 p-2 bg-dark-600/60 rounded-xl text-center mb-4 border border-white/5">
                  <div>
                    <span className="block text-xs font-bold text-white">{stats.total}</span>
                    <span className="text-[10px] text-white/40">Cards</span>
                  </div>
                  <div>
                    <span className="block text-xs font-bold text-amber-400">{stats.dueCount}</span>
                    <span className="text-[10px] text-white/40">Due</span>
                  </div>
                  <div>
                    <span className="block text-xs font-bold text-emerald-400">{stats.masteredCount}</span>
                    <span className="text-[10px] text-white/40">Mastered</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  <div className="flex gap-1">
                    <button
                      onClick={() => exportToAnkiCSV(deck)}
                      title="Export to Anki (CSV)"
                      className="px-2 py-1 text-white/50 hover:text-saffron-400 hover:bg-white/5 rounded-lg text-xs flex items-center gap-1 transition-all"
                    >
                      <Download className="w-3.5 h-3.5" /> Anki
                    </button>
                    <button
                      onClick={() => exportToJSON(deck)}
                      title="Export JSON"
                      className="px-2 py-1 text-white/50 hover:text-saffron-400 hover:bg-white/5 rounded-lg text-xs transition-all"
                    >
                      JSON
                    </button>
                    <button
                      onClick={() => onDeleteDeck(deck.id)}
                      title="Delete deck"
                      className="p-1 text-white/30 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-all"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <button
                    onClick={() => onSelectDeck(deck)}
                    className="btn-primary text-xs py-1.5 px-4 font-semibold shadow-sm flex items-center gap-1.5"
                  >
                    <Play className="w-3.5 h-3.5" /> Study Deck
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
